// Page: CampaignSettings
// Purpose: Edit campaign settings and survey questions for a given campaign ID
// within the current namespace. Handles read-only gating, loads campaign data,
// initializes form via hook, and shows a live phone preview.
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSettingsForm } from '../../../../hooks/useSettingsForm';
import { SettingsFormContainer } from '../../../../components/testimonials/campaignSettings/components/SettingsFormContainer';
import { ArrowLeft, Eye, Shield, Users } from 'lucide-react';
import { useToast } from '../../../../components/ui/notifications/toast-notification.jsx';
import { useNamespace } from '../../../../context/namespace';
import { get } from '../../../../api/core';
import { PhonePreview } from '../../../../components/create-campaign/preview/PhonePreview';
import { themes } from '../../../../components/create-campaign/utils/campaignThemes';
import { CampaignSettingsSkeleton } from '../../../../components/ui/feedback/skeleton.jsx';
import { Card, CardContent, CardHeader } from '../../../../components/ui/layout/card';

// Read-only permission message component
const ReadOnlyPermissionMessage = ({ userPermission, currentNamespace, campaignName }) => {
  return (
    <Card className="w-full border border-gray-200 dark:border-gray-800 shadow-sm">
      <CardHeader className="pb-3">
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800/60 dark:bg-amber-900/20">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex-shrink-0">
              <Eye className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white">
                Read-Only Access
              </h3>
              <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                You currently have read-only access to the <span className="font-medium">{currentNamespace}</span> namespace.
              </p>
            </div>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Your Options</h3>
          
          <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700 transition-colors bg-white dark:bg-gray-900">
            <div className="flex items-start gap-3">
              <div className="mt-1 flex-shrink-0 rounded-full bg-amber-100 p-1.5 dark:bg-amber-900/30">
                <Shield className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white">Contact Administrator</h4>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  Contact your namespace administrator to request Editor or Admin permissions for this namespace.
                </p>
              </div>
            </div>
          </div>

          <div className="p-4 rounded-lg border border-primary-200 bg-primary-50 dark:border-primary-800/60 dark:bg-primary-900/20">
            <div className="flex items-start gap-3">
              <div className="mt-1 flex-shrink-0 rounded-full bg-primary-100 p-1.5 dark:bg-primary-900/50">
                <Users className="h-4 w-4 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white">Switch Namespace</h4>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  Switch to another namespace where you have Editor or Admin permissions to create or modify campaigns.
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Permission Levels</h3>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">Read-Only:</span>
              <span className="font-medium text-gray-900 dark:text-white">View campaigns only</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">Editor:</span>
              <span className="font-medium text-gray-900 dark:text-white">Create and edit campaigns</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">Admin:</span>
              <span className="font-medium text-gray-900 dark:text-white">Full access including user management</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Map the original step indices to the new ones (skipping category and subcategory)
const mapStepIndex = (originalIndex) => {
  // Original steps: 0=Internal, 1=Category, 2=Subcategory, 3=Design, 4=BasicInfo, 5=Details, 6=Review
  // New steps: 0=Internal, 1=Design, 2=BasicInfo, 3=Details, 4=Review
  const mapping = {
    0: 0, // Internal Name -> Internal Name
    1: 1, // Category -> Design (skip)
    2: 1, // Subcategory -> Design (skip)
    3: 1, // Design -> Design
    4: 2, // BasicInfo -> BasicInfo
    5: 3, // Details -> Details
    6: 4  // Review -> Review
  };
  return mapping[originalIndex] || 0;
};

// Total number of steps in the settings flow (reduced by 2 for category and subcategory)
const TOTAL_STEPS = 5;

export default function CampaignSettings() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams(); // Get campaign ID from URL path parameter
  const { addToast } = useToast();
  const { currentNamespace, namespaces } = useNamespace();
  const [campaignData, setCampaignData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fetchAttempted, setFetchAttempted] = useState(false); // Track if we've attempted to fetch
  const [formInitError, setFormInitError] = useState(null);

  const campaignId = id; // Use the ID from URL path parameter
  
  // Get the current namespace ID and user permission
  const currentNamespaceObj = namespaces.find(ns => ns.name === currentNamespace);
  const namespaceId = currentNamespaceObj?.id || null;
  const userPermission = currentNamespaceObj?.currentUserPermission || null;

  // Debug permissions to console
  console.log('CampaignSettings permission check:', {
    currentNamespace,
    userPermission,
    namespaceId,
    isLoading
  });

  // Check if user has read-only permission for the current namespace
  const hasReadOnlyPermission = userPermission === 'readonly' || userPermission === 'read-only';

  // Fetch campaign data
  useEffect(() => {
    const fetchCampaignData = async () => {
      if (!campaignId) {
        setIsLoading(false);
        setError('Campaign ID is required');
        return;
      }
      
      if (!namespaceId) {
        setIsLoading(false);
        setError('Namespace ID is required');
        return;
      }

      // Prevent multiple fetch attempts
      if (fetchAttempted) return;
      setFetchAttempted(true);

      try {
        // Use the get utility function instead of fetch directly
        // Include namespaceId as a query parameter
        const data = await get(`/campaign/campaigns/${campaignId}?namespaceId=${namespaceId}`);
        
        if (data) {
          // Log explainer video information
          console.log('CampaignSettings: Campaign data received with explainer video info:', { 
            hasExplainerVideo: data.hasExplainerVideo,
            explainerVideo: data.explainerVideo,
            videoUrl: data.videoUrl
          });
          
          setCampaignData(data);
        } else {
          throw new Error('Campaign not found');
        }
      } catch (err) {
        console.error('Error fetching campaign data:', err);
        setError(err.message || 'An error occurred while fetching campaign data');
        addToast(err.message || 'Failed to load campaign data', 'error');
      } finally {
        setIsLoading(false);
      }
    };

    if (currentNamespace && namespaceId && !fetchAttempted) {
      fetchCampaignData();
    }
  }, [campaignId, currentNamespace, namespaceId, addToast, fetchAttempted]);

  // Initialize form with campaign data
  let settingsForm;
  
  try {
    settingsForm = useSettingsForm(campaignData);
  } catch (err) {
    console.error('Error initializing settings form:', err);
    if (!formInitError) {
      setFormInitError(err.message || 'Failed to initialize campaign settings form');
      addToast(err.message || 'Failed to initialize campaign settings form', 'error');
    }
  }

  // Expose setPreviewImage globally for direct access from components
  useEffect(() => {
    // Expose the setPreviewImage function globally
    window.setGlobalPreviewImage = (imageData) => {
      console.log('CampaignSettings: Global setPreviewImage called with:', imageData ? 'image data' : 'null');
      if (settingsForm && typeof settingsForm.setPreviewImage === 'function') {
        settingsForm.setPreviewImage(imageData);
      }
    };
    
    // Clean up when component unmounts
    return () => {
      delete window.setGlobalPreviewImage;
    };
  }, [settingsForm]);

  if (isLoading) {
    return <CampaignSettingsSkeleton />;
  }

  if (error || !campaignData || formInitError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <h1 className="text-2xl font-bold text-red-600 dark:text-red-400">Error</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">{formInitError || error || 'Campaign not found'}</p>
        <button
          onClick={() => navigate('/app/campaigns')}
          className="mt-4 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors"
        >
          Back to Campaigns
        </button>
      </div>
    );
  }

  if (!settingsForm) {
    return <CampaignSettingsSkeleton />;
  }

  // Map the current step to the new step index for progress calculation
  const mappedStep = mapStepIndex(settingsForm.currentStep);
  const progressPercentage = ((mappedStep + 1) / TOTAL_STEPS) * 100;

  // If user has read-only permission, show the permission error
  if (hasReadOnlyPermission && !isLoading && namespaceId) {
    return (
      <div className="p-6 w-full max-w-[1600px] mx-auto">
        <div className="space-y-4">
          <div>
            <div className="flex items-center mb-4">
              <button
                onClick={() => navigate(`/app/campaigns/${campaignId}`)}
                className="mb-6 inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Campaign
              </button>
            </div>
            
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Campaign Settings</h1>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 mb-4">
              View campaign settings and survey questions.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-[1fr,auto] gap-6 items-start">
            <ReadOnlyPermissionMessage
              userPermission={userPermission}
              currentNamespace={currentNamespace}
              campaignName={campaignData?.name}
            />
            
            {/* Show phone preview with campaign data in read-only mode */}
            {settingsForm.showPreview && (
              <>
                {console.log('CampaignSettings: Rendering PhonePreview with currentStep:', {
                  originalStep: settingsForm.currentStep,
                  mappedStep: mapStepIndex(settingsForm.currentStep),
                  showingQuestions: settingsForm.currentStep === 5,
                  hasExplainerVideo: settingsForm.hasExplainerVideo,
                  explainerVideoExists: !!settingsForm.explainerVideo
                })}
                <PhonePreview
                  selectedTheme={settingsForm.selectedTheme}
                  themes={themes}
                  previewImage={settingsForm.previewImage}
                  formData={settingsForm.formData}
                  surveyQuestions={settingsForm.surveyQuestions}
                  currentStep={settingsForm.currentStep}
                  gradientColors={settingsForm.gradientColors}
                  gradientDirection={settingsForm.gradientDirection}
                  hexText={settingsForm.hexText}
                  hasExplainerVideo={settingsForm.hasExplainerVideo}
                  explainerVideo={settingsForm.explainerVideo}
                  isSettingsFlow={true}
                />
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 w-full max-w-[1600px] mx-auto">
      <div className="space-y-4">
        <div>
          <div className="flex items-center mb-4">
            <button
              onClick={() => navigate(`/app/campaigns/${campaignId}`)}
              className="mb-6 inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Campaign
            </button>
          </div>
          
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Edit Campaign</h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 mb-4">
            Update your campaign settings and customize your survey questions.
          </p>
          
          <div className="relative max-w-[800px] h-1 bg-gray-200 dark:bg-gray-800 mt-4">
            <div
              className="absolute inset-y-0 left-0 bg-primary-600 dark:bg-primary-400 transition-all duration-500"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[800px,auto] gap-6 items-start">
          <SettingsFormContainer
            {...settingsForm}
            campaignId={campaignId}
            namespaceId={namespaceId}
            readOnly={hasReadOnlyPermission}
          />

          {settingsForm.showPreview && (
            <>
              {console.log('CampaignSettings: Rendering PhonePreview with currentStep:', {
                originalStep: settingsForm.currentStep,
                mappedStep: mapStepIndex(settingsForm.currentStep),
                showingQuestions: settingsForm.currentStep === 5,
                hasExplainerVideo: settingsForm.hasExplainerVideo,
                explainerVideoExists: !!settingsForm.explainerVideo
              })}
              <PhonePreview
                selectedTheme={settingsForm.selectedTheme}
                themes={themes}
                previewImage={settingsForm.previewImage}
                formData={settingsForm.formData}
                surveyQuestions={settingsForm.surveyQuestions}
                currentStep={settingsForm.currentStep}
                gradientColors={settingsForm.gradientColors}
                gradientDirection={settingsForm.gradientDirection}
                hexText={settingsForm.hexText}
                hasExplainerVideo={settingsForm.hasExplainerVideo}
                explainerVideo={settingsForm.explainerVideo}
                isSettingsFlow={true}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
